"use client"
import Hero from "@/components/pages/home/Hero";
import Services from "@/components/pages/home/Services/Services";
import { useEffect, useState, useCallback, useRef } from "react";
import ServicesHead from "@/components/pages/home/Services/ServicesHead";
import Heading from "@/components/pages/home/Heading";
import { useTranslations } from "next-intl";
import WhyUs from "@/components/pages/home/WhyUs/WhyUs";
import YourJourney from "@/components/pages/home/YourJourney/YourJourney";
import Consultation from "@/components/pages/home/Consultation/Consultation";
import Testimonials from "@/components/pages/home/Testimonials/Testimonials";
import CommonQuestions from "@/components/pages/home/CommonQuestions/CommonQuestions";
import Pricing from "@/components/pages/home/Pricing/Pricing";
import SuccessStories from "@/components/pages/home/SuccessStories/SuccessStories";
import ProductsArea from "@/components/pages/home/ProductsArea";
import ScrollFadeIn from "@/components/shared/animations/ScrollFadeIn";
import AppPromo from "@/components/pages/home/AppPromo/AppPromo";
import AppPromoModal from "@/components/pages/home/AppPromo/AppPromoModal";
import { userProductsStore } from "@/store/productsStore";
import { usePricesStore } from "@/store/pricesStore";
import PricingHead from "@/components/pages/home/Pricing/PricingHead";
import { useTransformStore } from "@/store/transformsStore";
import dynamic from 'next/dynamic';

// Move dynamic import before the component
const DynamicCalScheduler = dynamic(
    () => import("@/components/features/Calendar/CalScheduler"),
    {
        loading: () => (
            <div className="w-full h-[450px] md:h-[550px] lg:h-[650px] animate-pulse bg-gray-100 rounded-2xl" />
        ),
        ssr: false
    }
);

export default function HomePage() {
    const [serviceType, setServiceType] = useState<string>('people');
    const [pricingType, setPricingType] = useState<string>('people');
    const dataFetched = useRef(false);

    const handleServiceType = (type: string) => {
        setServiceType(type);
    };

    const handlePricingType = (type: string) => {
        setPricingType(type);
    };

    const { getProducts } = userProductsStore()
    const { getPricesAndAnalytics } = usePricesStore()
    const { getAllTransforms } = useTransformStore()

    useEffect(() => {
        if (!dataFetched.current) {
            const fetchData = async () => {
                try {
                    await Promise.all([
                        getPricesAndAnalytics(),
                        getProducts(),
                        getAllTransforms()
                    ]);
                } catch (error) {
                    console.error('Error fetching initial data:', error);
                }
            };
            fetchData();
            dataFetched.current = true;
        }
    }, []);

    const t = useTranslations("Home")
    // Default OFF unless explicitly enabled
    const APP_PROMO_ENABLED = process.env.NEXT_PUBLIC_APP_PROMO_ENABLED === 'true'

    return (
        <div>
            <Hero />
            <div className="flex flex-col gap-24 py-20 px-10 md:px-28">
                <ScrollFadeIn>
                    <ServicesHead id="services" serviceType={serviceType} handleServiceType={handleServiceType} />
                </ScrollFadeIn>
                <Services serviceType={serviceType} />

                <ScrollFadeIn>
                    <Heading id="whyus" title={t("whyUs.title")} subTitle={t("whyUs.subTitle")} desc="" />
                </ScrollFadeIn>
                <WhyUs />

                <ScrollFadeIn>
                    <Heading id="transforms" title={t("success-stories.title")} subTitle={t("success-stories.subTitle")} desc="" />
                </ScrollFadeIn>
                <SuccessStories />

                <ScrollFadeIn>
                    <Heading id="journey" title={t("your-journey.title")} subTitle={t("your-journey.subTitle")} desc={t("your-journey.desc")} />
                </ScrollFadeIn>
                <YourJourney />

                <ScrollFadeIn>
                    <Heading title={t("products.title")} subTitle={t("products.subTitle")} desc={t("products.desc")} />
                </ScrollFadeIn>
                <ProductsArea />

                <ScrollFadeIn>
                    <PricingHead pricingType={pricingType} handlePricingType={handlePricingType} />
                </ScrollFadeIn>
                <Pricing pricingType={pricingType} />

                <ScrollFadeIn>
                    <Heading title="" subTitle={t("consultation.title")} />
                </ScrollFadeIn>
                <ScrollFadeIn>
                    <Consultation buttonText={t('consultation.button')} />
                </ScrollFadeIn>

                <Testimonials />

                <ScrollFadeIn>
                    <CommonQuestions />
                </ScrollFadeIn>

                <ScrollFadeIn>
                    <AppPromo appleUrl="https://apps.apple.com" googleUrl="https://play.google.com" />
                </ScrollFadeIn>

                <DynamicCalScheduler />
                {APP_PROMO_ENABLED && (
                    <AppPromoModal enabled={APP_PROMO_ENABLED} appleUrl="https://apps.apple.com" googleUrl="https://play.google.com" />
                )}
            </div>
        </div>
    );
}